import { useId, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, X } from 'lucide-react';
import {
  useDialog,
  Button,
  Textarea,
  PersonBadge,
  LeaveBalanceBanner,
  LeaveDecisionPanel,
} from '@gokrew/ui';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import {
  MyTeamDecision,
  type MyTeamApprovalRequest,
} from '@gokrew/models/my-team';
import { toBadgePerson } from '@/utils/my-team';
import {
  useDecideMyTeamLeave,
  useMyTeamApprovalContext,
} from '@/hooks/my-team/useMyTeam';
import { useConfirmApprove } from '../useConfirmApprove';
import { toApprovalNotice } from '../approvalNotice';
import { ApprovalCalendar } from '../ApprovalCalendar';
import { useTranslation } from 'react-i18next';
import { DateDisplay } from '@gokrew/ui/components/DateRangeDisplay';
import { TranslationNS } from '@gokrew/models/constants';
import type { Meridiem } from '@gokrew/models/leaves';
import styles from './ApprovalDetails.module.css';

/**
 * The decision-ready view of one request in the Approvals inbox: the
 * balance banner on top, then the request, the reporting manager, how long
 * it has waited and who else on the team is off those dates. Read from
 * `employee/my-team/approvals/leave/:id/context`, which answers only an
 * approver who may decide this request.
 */
export const ApprovalDetails = ({
  request,
  withActions = false,
  dialogId,
}: {
  request: MyTeamApprovalRequest;
  withActions?: boolean;
} & Pick<DialogComponentProps, 'dialogId'>) => {
  const { t, i18n } = useTranslation(TranslationNS.MyTeam);
  const {
    data: context,
    isLoading,
    isError,
  } = useMyTeamApprovalContext(request.id);

  const decide = useDecideMyTeamLeave();
  const confirmApprove = useConfirmApprove();
  const { closeDialog } = useDialog();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const reasonId = useId();
  const submit = async (decision: MyTeamDecision) => {
    setError('');
    try {
      await decide.mutateAsync({
        leaveRequestId: request.id,
        decision,
        comment: rejecting ? reason.trim() : undefined,
      });
      toast.success(
        t(
          decision === MyTeamDecision.Approve
            ? 'approvals.approved_toast'
            : 'approvals.rejected_toast',
          { name: request.employeeName }
        )
      );
      if (dialogId) closeDialog(dialogId);
    } catch (err) {
      const notice = toApprovalNotice(err, request, t);
      setError([notice.title, notice.message].filter(Boolean).join(' '));
    }
  };
  const [firstName, ...last] = request.employeeName.split(' ');
  return (
    <div>
      <PersonBadge
        person={toBadgePerson({
          employeeId: request.employeeId ?? request.id,
          firstName,
          lastName: last.join(' '),
        })}
        subtitle={request.jobTitle}
        size="lg"
      />
      <div className={styles.layout}>
        <div className={styles.body}>
          <LeaveBalanceBanner
            balance={context?.balanceCheck ?? request.balanceCheck}
          />
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt>{t('approvals.leave_type')}</dt>
              <dd className={styles.strong}>{request.leaveType ?? ''}</dd>
            </div>
            <div className={styles.fact}>
              <dt>{t('approvals.dates')}</dt>
              <dd>
                <DateDisplay
                  days={request.days}
                  startDate={request.startDate}
                  endDate={request.endDate}
                  startSession={request.startSession as Meridiem | null}
                  endSession={request.endSession as Meridiem | null}
                  startTime={request.startTime}
                  endTime={request.endTime}
                  locale={i18n.language}
                  compact
                />
              </dd>
            </div>
            {request.reason && (
              <div className={styles.fact}>
                <dt>{t('approvals.reason')}</dt>
                <dd>{request.reason}</dd>
              </div>
            )}
          </dl>
          {isError && <p role="alert">{t('approvals.error')}</p>}
          <LeaveDecisionPanel
            context={context}
            isLoading={isLoading}
            withBalance={false}
          />
        </div>
        <ApprovalCalendar request={request} context={context} />
      </div>
      {withActions && (
        <div className={styles.footer}>
          {error && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          {rejecting && (
            <div className={styles.reason}>
              <label htmlFor={reasonId}>
                {t('workspace.rejection_reason')}
              </label>
              <Textarea
                id={reasonId}
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                disabled={decide.isPending}
              />
            </div>
          )}
          <div className={styles.actions}>
            {rejecting && (
              <Button
                variant="outline"
                disabled={decide.isPending}
                onClick={() => setRejecting(false)}
              >
                {t('to_approve.cancel')}
              </Button>
            )}
            <Button
              variant="dangerOutline"
              IconPrefix={X}
              disabled={decide.isPending || (rejecting && !reason.trim())}
              onClick={() => {
                if (!rejecting) setRejecting(true);
                else void submit(MyTeamDecision.Reject);
              }}
            >
              {t('approvals.reject')}
            </Button>
            {!rejecting && (
              <Button
                variant="primary"
                IconPrefix={Check}
                disabled={decide.isPending || isLoading || isError}
                onClick={() =>
                  confirmApprove(
                    {
                      ...request,
                      balanceCheck:
                        context?.balanceCheck ?? request.balanceCheck,
                    },
                    () => {
                      void submit(MyTeamDecision.Approve);
                    }
                  )
                }
              >
                {t('approvals.approve')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
